import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/auth-context";
import { useAuth } from "@/lib/auth-context";

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid | failed | expired

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      } catch (e) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      }
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div
      data-testid="billing-success-page"
      className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6"
    >
      <div className="max-w-lg text-center">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-[#0A192F] mx-auto animate-spin" />
            <h1 className="mt-6 font-heading text-3xl font-bold text-[#0A192F]">
              Confirming your subscription…
            </h1>
            <p className="mt-3 text-slate-600">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="w-16 h-16 mx-auto rounded-full bg-[#0A192F] flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#C5A059]" />
            </span>
            <h1 className="mt-6 font-heading text-3xl font-bold text-[#0A192F]">
              You&apos;re in.
            </h1>
            <p className="mt-3 text-slate-600">
              Your subscription is active. Welcome to the LexIntake attorney network.
            </p>
            <Button
              data-testid="billing-success-dashboard-btn"
              onClick={() => navigate("/attorney/dashboard")}
              className="mt-8 bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-6 py-5"
            >
              Go to dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="mt-6 font-heading text-3xl font-bold text-[#0A192F]">
              We couldn&apos;t confirm payment.
            </h1>
            <p className="mt-3 text-slate-600">
              {status === "expired"
                ? "The checkout session expired. Please try again."
                : "Something went wrong. If you completed checkout, please refresh."}
            </p>
            <Button
              data-testid="billing-success-retry-btn"
              onClick={() => navigate("/billing")}
              className="mt-8 bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
            >
              Back to billing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
